import React from 'react';
import { Plus, Wallet } from 'lucide-react';
import { useCurrency } from '@/hooks/useCurrency';
import { useTranslation } from 'react-i18next';

interface AccountBalance {
    amount: number;
    currency: string;
}

interface BalanceDisplayProps {
    isAuthenticated: boolean;
    isBalanceLoading: boolean;
    accountBalance?: AccountBalance | null;
    isVerified: boolean;
    isCallActive: boolean;
    onTopUp: () => void;
    onLogin: () => void;
}

export const BalanceDisplay: React.FC<BalanceDisplayProps> = ({
    isAuthenticated,
    isBalanceLoading,
    accountBalance,
    isVerified,
    isCallActive,
    onTopUp,
    onLogin,
}) => {
    const { t } = useTranslation('app');
    const { formatCurrency } = useCurrency();

    if (!isAuthenticated) {
        return (
            <button
                onClick={onLogin}
                className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs font-medium shadow-sm transition-colors hover:bg-muted whitespace-nowrap"
            >
                <Plus className="w-3.5 h-3.5 shrink-0" />
                <span>{t('dialer.addCredit')}</span>
            </button>
        );
    }

    if (isBalanceLoading) {
        return (
            <div className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs font-medium shadow-sm whitespace-nowrap">
                <div className="rounded-full w-2 h-2 bg-current animate-pulse" />
                <span>{t('dialer.loadingBalance')}</span>
            </div>
        );
    }

    if (!accountBalance) {
        return (
            <div className="inline-flex items-center gap-1 rounded-full border border-destructive/60 bg-destructive/10 px-3 py-1.5 text-xs font-medium shadow-sm text-destructive whitespace-nowrap">
                <div className="rounded-full w-2 h-2 bg-current" />
                <span>{t('dialer.balanceUnavailable')}</span>
            </div>
        );
    }

    const balanceContent = (
        <>
            <Wallet className="w-3.5 h-3.5 shrink-0" />
            <span>{formatCurrency(accountBalance.amount / 100)}</span>
        </>
    );

    return (
        <div className="flex flex-col items-center gap-1">
            {isCallActive ? (
                <div className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs font-medium shadow-sm whitespace-nowrap">
                    {balanceContent}
                </div>
            ) : (
                <button
                    onClick={onTopUp}
                    className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs font-medium shadow-sm transition-colors hover:bg-muted whitespace-nowrap"
                >
                    {balanceContent}
                </button>
            )}
            {!isVerified && (
                <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] text-amber-600 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                    {t('account.statusVerificationPending')}
                </span>
            )}
        </div>
    );
};
